import * as yup from "yup";

import {
  generateIdempotencyKey,
  ISystemStory,
  SUBTYPES,
  TStoryContentElementType,
  TWorkflowAction,
  WORKFLOW_ACTIONS,
} from "@dl/declarations";
import { TEditorStoryVersion } from "../types";

export const LIMITS = {
  HEADLINE: {
    MIN: 8,
    MAX: 200,
  },
  SUBHEADLINE: {
    MAX: 150,
  },
  DESCRIPTION: {
    MAX: 300,
  },
};

/** Story */

export type StoryFormData = Pick<
  ISystemStory,
  | "headline"
  | "subheadline"
  | "description"
  | "meta_data"
  | "content_elements"
  | "workflow"
  | "sub_vertical"
  | "guests"
  | "main_asset_info"
  | "idempotency_key"
> & {
  subtype: string;
  verticals: string[];
  primary_vertical: string;
  authors: string[];
  tags: string[];
  main_asset: string;
  homepage_asset: string;
  social_media_asset: string;
  collections: string[];

  // content_elements_has_changed: boolean;
  // content_elements_are_loaded: boolean;
};

export const initialValues: StoryFormData = {
  headline: "",
  subheadline: "",
  description: "",
  subtype: "",
  primary_vertical: "",
  sub_vertical: "internal",
  verticals: [],
  authors: [],
  guests: [],
  tags: [],
  content_elements: [],

  main_asset: "",
  homepage_asset: "",
  social_media_asset: "",

  meta_data: {
    title: "",
    description: "",
  },
  collections: [],

  workflow: "none",

  idempotency_key: generateIdempotencyKey(),

  // content_elements_has_changed: false,
  // content_elements_are_loaded: false,
};

export const commonStorySchema = yup.object().shape({
  headline: yup
    .string()
    .required()
    .min(LIMITS.HEADLINE.MIN)
    .max(LIMITS.HEADLINE.MAX)
    .label("Headline"),

  idempotency_key: yup.string().required().label("Idempotency Key")
});

export const createStorySchema = commonStorySchema
  .shape({
    subheadline: yup
      .string()
      .optional()
      .max(LIMITS.SUBHEADLINE.MAX)
      .label("Subheadline"),
    description: yup
      .string()
      .optional()
      // .max(LIMITS.DESCRIPTION.MAX)
      .label("Description"),

    subtype: yup.string().required().label("Story Subtype"),
    verticals: yup
      .array()
      .of(yup.string())
      .required()
      .min(1)
      .label("Verticals")
      .test(
        "only-one-research-vertical",
        "Only one research vertical is allowed",
        (value, context) => {
          if (context?.parent?.subtype === SUBTYPES.RESEARCH.ID) {
            return value.length === 1;
          }

          return true;
        },
      ),
    primary_vertical: yup.string().required().label("Primary Vertical"),
    sub_vertical: yup.string().required().label("Sub Vertical"),
    authors: yup.array().of(yup.string()).label("Authors"),
    tags: yup.array().of(yup.string()).label("Tags"),
    guests: yup
      .array()
      .of(
        yup.object().shape({
          first_name: yup.string().required().label("Guest First Name"),
          last_name: yup.string().label("Guest Last Name"),
        }),
      )
      .label("Guests"),

    main_asset: yup.string().required().label("Main Image"),
    homepage_asset: yup.string().optional().label("Homepage Image"),
    social_media_asset: yup.string().optional().label("Social media Image"),

    main_asset_info: yup
      .object()
      .optional()
      .shape({
        _id: yup.string().label("Image ID"),
        src_url: yup.string().label("Image Source URL"),
        headline: yup.string().label("Image Headline"),
        copyright: yup.string().label("Image Copyright"),
        credit: yup.string().label("Image Credit"),
        article_caption: yup.string().label("Image Caption"),
      })
      .default(undefined),

    meta_data: yup.object().shape({
      title: yup.string().optional().label("Meta Title"),
      description: yup.string().optional().label("Meta Description"),
    }),

    content_elements: yup
      .array()
      .of(yup.object<TStoryContentElementType>())
      .required()
      .label("Story content"),

    collections: yup.array().of(yup.string()).optional().label("Collections"),
    workflow: yup.string().label("Workflow"),
  })
  .test(
    "at-least-one-author-or-one-guest",
    "At least one author or one guest is required",
    (value) => {
      return (
        (value?.authors?.length || 0) > 0 || (value?.guests?.length || 0) > 0
      );
    },
  );

export const generateStoryFormState = (
  story: TEditorStoryVersion,
): Omit<StoryFormData, "workflow"> => {
  const usedVersion = {
    ...story,
    ...story?.savedStoryVersionObject,
  };

  const {
    headline,
    subheadline,
    description,
    subtype,
    verticals,
    primary_vertical,
    sub_vertical,
    authors,
    guests,
    meta_data,
    tags,
    main_asset,
    homepage_asset,
    social_media_asset,
    main_asset_info,
    content_elements,
    idempotency_key
  } = usedVersion;

  const collectionsObjects = story?.collectionsObjects || [];

  const state = {
    headline,
    subheadline,
    description,

    meta_data: {
      title: meta_data?.title || "",
      description: meta_data?.description || "",
    },

    subtype: subtype || "",
    verticals: verticals || [],
    primary_vertical: primary_vertical || "",
    sub_vertical: sub_vertical || "internal",

    authors: authors || [],
    tags: tags || [],

    guests: guests || [],

    main_asset: main_asset || "",
    homepage_asset: homepage_asset || "",
    social_media_asset: social_media_asset || "",

    main_asset_info: main_asset_info || undefined,

    content_elements: content_elements || [],

    publish_date: "immediate",
    display_date: "immediate",

    collections: (collectionsObjects || []).map(
      (collection) => collection.collection_ref,
    ),

    idempotency_key: idempotency_key || generateIdempotencyKey(),

    /**
     * Editor state is not monitored by the form, so the following workaround is applied:
     * 1. content_elements_are_loaded to notify that the initial values are loaded
     * 2. content_elements_has_changed to track if the content elements have been modified
     */
    // content_elements_has_changed: false,
    // content_elements_are_loaded: false,
  };

  return state;
};

/** Workflow */

export type StoryWorkflowFormData = {
  type: NonNullable<TWorkflowAction>;
  publish_date_value: string;
  display_date_value: string;
};

export const workflowSchema = yup.object().shape({
  type: yup
    .string()
    .oneOf([
      WORKFLOW_ACTIONS.Publish,
      WORKFLOW_ACTIONS.Update,
      WORKFLOW_ACTIONS.Unpublish,
    ])
    .required()
    .label("Workflow Action"),

  publish_date_value: yup
    .string()
    .label("Publish Date Value")
    .test(
      "is-immediate-or-date",
      "Value must be 'now' or a valid future date",
      (value) => {
        if (value === "immediate") return true;
        // Check if value is a valid Date or ISO string
        const date = new Date(String(value));
        const isValid = !isNaN(date.getTime());

        if (!isValid) return false;

        const isInFuture = date.getTime() > new Date().getTime();

        if (!isInFuture) return false;

        return true;
      },
    )
    .required()
    .label("Workflow Value"),

  display_date_value: yup
    .string()
    .label("Display Date Value")
    .test(
      "is-immediate-or-date",
      "Value must be 'now' or a valid future date",
      (value) => {
        if (value === "immediate") return true;
        // Check if value is a valid Date or ISO string
        const date = new Date(String(value));
        const isValid = !isNaN(date.getTime());

        if (!isValid) return false;

        // const isInFuture = date.getTime() > new Date().getTime();

        // if (!isInFuture) return false;

        // const isBeforePublishDate =
        //   context?.parent?.publish_date_value !== "immediate"
        //     ? date.getTime() <=
        //       new Date(context?.parent?.publish_date_value).getTime()
        //     : true;

        // if (!isBeforePublishDate) return false;

        return true;
      },
    )
    .required()
    .label("Display Date Value"),
});

/** Review */
export type StoryReviewFormData = {
  assigned_to: string[];
  comment: string;
};

export const reviewSchema = yup.object().shape({
  assigned_to: yup
    .array()
    .of(yup.string().required())
    .min(1)
    .label("Assign To")
    .required(),
  comment: yup.string().required().min(5).max(250).label("Comment"),
});
